<template>
	<div class="account-buttons">
		<Profile
			v-if="loggedIn"
			:user="user"
			:imgProfile="imgProfile"
			@logout="logout"
		/>
		<template v-else>
			<button
				:title="$t('LoginButtonTitleAttributeHeaderGetStarted')"
				:aria-label="$t('LoginButtonAriaLabelHeaderGetStarted')"
				class="get-started"
				data-test="login-button"
				@click="loginClick('signUp')"
			>
				<span
					class=""
					:class="{
						'wficon-user-circle-outlined': !hoverOnUserIcon,
						'wficon-user-circle': hoverOnUserIcon,
					}"
					@mouseenter="hoverOnUserIcon = true"
					@mouseleave="hoverOnUserIcon = false"
				></span>
			</button>
		</template>
		<sweet-modal
			ref="modal"
			@close="closeDialog"
			class="login-dialog"
			data-test="login-dialog"
		>
			<p class="dialog-caption">
				{{ $t(modalContent[dialogType].title) }}
			</p>
			<p class="dialog-text dialog-error" v-if="hasLoginError">
				{{ logInError }}
			</p>
			<p class="dialog-text" v-else>
				{{ $t(modalContent[dialogType].text) }}
			</p>
			<div v-if="isLoading || isLoggingIn" class="loading">
				<img
					src="/static-wf/images/ico-loading.svg"
					width="30"
					height="30"
					alt="Loading..."
				/>
			</div>
			<template v-else>
				<button
					:aria-label="$t(modalContent[dialogType].btnAriaLabel)"
					:title="$t(modalContent[dialogType].btnTitle)"
					class="google-btn"
					data-test="google-btn"
					@click="onLogin(dialogType)"
				>
					<span class="inline-text google-logo-wrapper">
						<img
							:src="googleLogo"
							alt="google-logo"
							class="google-logo"
							width="16"
							height="16"
						/>
					</span>
					{{ $t(modalContent[dialogType].btnText) }}
				</button>
			</template>
			<span
				class="inline-text"
				v-html="$t(modalContent[dialogType].disclaimerText)"
			/>
			<a href="#" class="link" @click.prevent="onRemindButtonClick">
				{{ $t(modalContent[dialogType].reminderText) }}
			</a>
		</sweet-modal>
		<sweet-modal
			ref="subscriptionModal"
			class="subscription-dialog"
			@close="closeSubscriptionDialog"
		>
			<p class="dialog-caption">Hey there, {{ userName }}!</p>
			<p class="dialog-text">
				Keep me in the loop! I’d like to receive exclusive email updates
				from WordFinder®
			</p>
			<button
				class="button button--subscription"
				@click="onSubscriptionAction(NEWSLETTERS.ON)"
			>
				Subscribe
			</button>
			<a
				href="#"
				class="link"
				@click.prevent="onSubscriptionAction(NEWSLETTERS.OFF)"
			>
				No, thanks
			</a>
		</sweet-modal>
		<div
			id="one-tap-container"
			data-prompt_parent_id="one-tap-container"
			style="margin-top: 20px;position: absolute; right: 0; top: 100%; z-index: 1001;"
		/>
	</div>
</template>

<script>
import Profile from "@/components/profile";
import { SweetModal } from "sweet-modal-vue";
import { mapActions, mapGetters, mapState } from "vuex";
import { NEWSLETTERS, GOOGLE_CLIENT_ID } from "@/common/consts";
import { isClientSide, readIndexedDb, isEmpty } from "@/common/functions";

const modalContent = {
	signIn: {
		name: "signUp",
		title: "LoginDialogTitleSignIn",
		text: "LoginDialogText1SignIn",
		btnText: "LoginButtonTextSignIn",
		btnAriaLabel: "LoginButtonAriaLabelSignIn",
		btnTitle: "LoginButtonTitleAttributeSignIn",
		hintLink: "LoginDialogTextSignIn",
		disclaimerText: "LoginDialogLegalDisclaimerSignIn",
		reminderText: "LoginDialogText3SignUp",
		eventDialogAction: "Click on header sign in button",
		eventDialogID: 39,
		eventLoginAction: "Click on Google sign in button",
		eventLoginID: 37,
		eventLabel: "LoginButtonTextHeaderSignIn",
	},
	signUp: {
		name: "signIn",
		title: "LoginDialogTitleSignUp",
		text: "LoginDialogText1SignUp",
		btnText: "LoginButtonTextSignUp",
		btnAriaLabel: "LoginButtonAriaLabelSignUp",
		btnTitle: "LoginButtonTitleAttributeSignUp",
		hint: "LoginDialogText2SignUp",
		hintLink: "LoginButtonTextHeaderSignIn",
		disclaimerText: "LoginDialogLegalDisclaimerSignUp",
		reminderText: "LoginDialogText3SignUp",
		eventDialogAction: "Click on header sign up button",
		eventDialogID: 40,
		eventLoginAction: "Click on Google sign up button",
		eventLoginID: 38,
		eventLabel: "LoginButtonTextHeaderGetStarted",
	},
};
export default {
	name: "AccountButtons",
	components: {
		SweetModal,
		Profile,
	},
	data() {
		return {
			modalContent,
			isLoading: false,
			NEWSLETTERS,
			hoverOnUserIcon: false,
		};
	},
	watch: {
		isDialogOpen: "toggleDialog",
		previouslyLoggedIn: "autoLoadLibrary",
		isSubscriptionDialogOpen: "openSubscriptionDialog",
	},
	computed: {
		...mapState({
			isLoggingIn: (state) => state.login.isLoggingIn,
			logInError: (state) => state.login.logInError,
			isSubscriptionDialogOpen: (state) =>
				state.login.isSubscriptionDialogOpen,
			newslettersSubscription: (state) =>
				state.login.user.newslettersSubscription,
		}),
		...mapGetters([
			"isDialogOpen",
			"dialogType",
			"user",
			"loggedIn",
			"imgProfile",
			"previouslyLoggedIn",
		]),
		googleLogo() {
			return isClientSide() ? "/static-wf/images/google-logo.svg" : "";
		},
		hasLoginError() {
			return !isEmpty(this.logInError);
		},
		userName() {
			return this.user?.data?.displayName;
		},
	},
	methods: {
		...mapActions([
			"login",
			"logout",
			"openDialog",
			"closeDialog",
			"toggleNewslettersSubscription",
		]),
		onOpenDialog() {
			this.$store.dispatch("trackEvent", {
				eventCategory: "Login",
				eventAction: modalContent[this.dialogType].eventDialogAction,
				eventLabel: modalContent[this.dialogType].eventLabel,
				eventID: modalContent[this.dialogType].eventDialogID,
			});
		},
		onLogin(type) {
			this.login();

			this.$store.dispatch("trackEvent", {
				eventCategory: "Login",
				eventAction: modalContent[type].eventLoginAction,
				eventLabel: modalContent[type].btnText,
				eventID: modalContent[type].eventLoginID,
			});
		},
		onRemindButtonClick() {
			this.remindButtonClick = true;
			this.$refs.modal.close();
			this.$store.dispatch("trackEvent", {
				eventCategory: "Login",
				eventAction: "Click on Remind me later button",
				eventLabel: "LoginDialogText3SignUp",
				eventID: 41,
			});
		},
		onCloseDialog() {
			if (!this.remindButtonClick) {
				this.$store.dispatch("trackEvent", {
					eventCategory: "Login",
					eventAction: "Click on sign in cross icon",
					eventID: 43,
				});
			}
			this.remindButtonClick = false;
		},
		onSubscriptionAction(value) {
			this.toggleNewslettersSubscription(value);
			const eventValues = {
				[NEWSLETTERS.ON]: {
					action: "Subscribe",
					label: "NewsletterSubscribeButtonClicked",
					id: 133,
				},
				[NEWSLETTERS.OFF]: {
					action: "No Thanks",
					label: "NewsletterNoThanksButtonClicked",
					id: 134,
				},
			};
			this.$store.dispatch("trackEvent", {
				eventCategory: "Newsletters",
				eventAction: `Click on Newsletter ${eventValues[value].action} Button`,
				eventLabel: eventValues[value].label,
				eventID: eventValues[value].id,
			});

			this.$refs.subscriptionModal.close();
		},
		toggleDialog(val) {
			if (val) {
				this.$refs.modal.open();
				this.onOpenDialog();
			} else {
				this.$refs.modal.close();
				this.onCloseDialog();
			}
		},
		openSubscriptionDialog(val) {
			if (val) {
				this.$refs.subscriptionModal.open();
				this.$store.dispatch("trackEvent", {
					eventCategory: "Newsletters",
					eventAction: "Newsletter subscription popup is shown",
					eventLabel: "NewsletterPopUpShown",
					eventID: 132,
				});
			}
		},
		closeSubscriptionDialog() {
			if (this.newslettersSubscription !== null) return;
			this.$store.dispatch("trackEvent", {
				eventCategory: "Newsletters",
				eventAction: "Newsletter subscription popup dismissed",
				eventLabel: "NewsletterPopUpDismissed",
				eventID: 135,
			});
		},
		async getFirebaseClient() {
			const firebase = await import("firebase/app");
			await import("firebase/auth");
			let firebaseConfig = await import("@/firebaseConfig");

			if (!firebase.apps.length) {
				firebase.initializeApp(firebaseConfig.default);
			}

			return firebase;
		},
		autoLoadLibrary(isPreviouslyLoggedIn) {
			if (
				typeof window.__PRERENDER_INJECTED === "undefined" &&
				isPreviouslyLoggedIn
			) {
				this.getFirebaseClient()
					.then((firebase) => {
						firebase.auth().onAuthStateChanged((user) => {
							this.$store.dispatch("fetchUser", user);
						});
					})
					.catch(console.log);
			}

			if (typeof google !== "undefined" && google) {
				setTimeout(() => {
					google.accounts.id.cancel();
				}, 500);
			}
		},
		loginClick(type) {
			this.isLoading = true;
			this.$store.commit("SET_LOG_IN_ERROR", null);
			if (typeof window.__PRERENDER_INJECTED === "undefined") {
				this.getFirebaseClient()
					.then((firebase) => {
						firebase.auth().onAuthStateChanged((user) => {
							this.$store.dispatch("fetchUser", user);
						});
						this.isLoading = false;
					})
					.catch(console.log);
				this.openDialog(type);
			}
		},
		handleOneTapResponse(token) {
			this.getFirebaseClient()
				.then((firebase) => {
					const credential = firebase.auth.GoogleAuthProvider.credential(
						token.credential
					);
					firebase
						.auth()
						.signInWithCredential(credential)
						.then((userCredential) => {
							this.$store.dispatch(
								"fetchUser",
								userCredential.user
							);
						});
				})
				.catch(console.log);

			this.$store.dispatch("trackEvent", {
				eventCategory: "Google One Tap Login",
				eventAction: "User logged in with One Tap",
			});
		},
		openOneTapDialog() {
			if (!this.previouslyLoggedIn) {
				google.accounts.id.initialize({
					client_id: GOOGLE_CLIENT_ID,
					callback: this.handleOneTapResponse,
					prompt_parent_id: "one-tap-container",
				});
				google.accounts.id.prompt((notification) => {
					// notification.h returns the boolean means popup was shown or not
					if (notification.h) {
						this.$store.dispatch("trackEvent", {
							eventCategory: "Google One Tap Login",
							eventAction: "Google One Tap Login Popup appears",
							nonInteraction: true,
						});
					}
					if (notification.isSkippedMoment()) {
						this.$store.dispatch("trackEvent", {
							eventCategory: "Google One Tap Login",
							eventAction: `${notification.l} One Tap Popup`,
						});
					}
				});
			}
		},
	},
	created() {
		readIndexedDb(
			"firebaseLocalStorageDb",
			"firebaseLocalStorage",
			this.$store
		);
		document.addEventListener("oneTapLogin", this.openOneTapDialog);
	},
};
</script>

<style lang="scss" scoped src="./styles.scss"></style>
